import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { faBooks } from '@fortawesome/pro-solid-svg-icons'
import { RankmiTable } from '@rankmi/rankmi-table'

import { IMainState } from 'store/states'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { IExternalCourseParticipant } from 'models/externalCourseParticipant.model'
import { canSkipOnRowSelected } from 'helpers'
import { useTranslationRankmi, joinKeys } from 'helpers/translations.helper'
import { downloadFile } from 'helpers/downloadFile.helper'
import { generateTableLocale } from 'helpers/translations.helper'
import { prefixTranslation } from '../../common'
import { useColumns } from './hooks/useColumns'
import styles from './index.module.scss'

type Id = IExternalCourseParticipant['id']

type DataColumn = IExternalCourseParticipant & {
  key: IExternalCourseParticipant['id']
  name: string
  action: Id
}

interface ITable {
  setPage(page: number): void
  setSearch(text: string): void
  setSort: any
  setPerPage(perPage: number): void
  setSelectedRows(ids: Id[]): void
  selectedRows: Id[]
  setFastFilters(value: number): void
  setAllSelected(state: boolean): void
  onDelete(id: Id): void
  onDeleteCertificate(id: number | string): void
}

export const Table: React.FC<ITable> = ({
  setPage,
  setSearch,
  setSort,
  setPerPage,
  setSelectedRows,
  selectedRows,
  setFastFilters,
  setAllSelected,
  onDelete: onDeleteBase,
  onDeleteCertificate: onDeleteCertificateBase,
}) => {
  const { t } = useTranslation()
  const tRelative = useTranslationRankmi({
    prefix: joinKeys(prefixTranslation, 'participants.table.header'),
  })

  const [dataSource, setDataSource] = useState<DataColumn[]>([])
  const [translations, setTranslations] = useState<any>({})
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false)
  const [
    showDeleteCertificateConfirmationModal,
    setShowDeleteCertificateConfirmationModal,
  ] = useState(false)

  const { list, total, loading } = useSelector(
    ({ externalCourseParticipants }: IMainState) => externalCourseParticipants
  )

  useEffect(() => {
    setTranslations(() =>
      generateTableLocale({
        header: {
          rowsCount: tRelative('rowsCount'),
          rowsCount_plural: tRelative('rowsCount_plural'),
        },
      })
    )
  }, [tRelative])

  const [selected, setSelected] = useState<
    undefined | IExternalCourseParticipant
  >()

  useEffect(() => {
    setDataSource(
      list.map(({ id, ...rest }) => ({
        id,
        key: id,
        name: rest.user.name,
        ...rest,
        action: id,
        hasCertificate: rest.certificate !== null ? true : false,
      }))
    )
  }, [list])

  const onDelete = (participant: IExternalCourseParticipant) => {
    setShowDeleteConfirmationModal(true)
    setSelected(participant)
  }

  const onDeleteCertificate = async (
    participant: IExternalCourseParticipant
  ) => {
    if (participant.certificate) {
      setShowDeleteCertificateConfirmationModal(true)
      setSelected(participant)
    }
  }

  const onDownloadCertificate = ({
    certificate,
  }: IExternalCourseParticipant) => {
    if (certificate) {
      downloadFile(certificate.url, 'certificate')
    }
  }

  const columns = useColumns({
    onDelete,
    onDeleteCertificate,
    onDownloadCertificate,
  })

  const handlePreDelete = (participant: IExternalCourseParticipant) => {
    onDeleteBase(participant.id)
    setShowDeleteConfirmationModal(false)
  }

  const handlePreDeleteCertificate = ({
    certificate,
  }: IExternalCourseParticipant) => {
    if (certificate) {
      onDeleteCertificateBase(certificate.id)
      setShowDeleteCertificateConfirmationModal(false)
    }
  }

  return (
    <div className={styles.container}>
      <RankmiTable
        columnHeaders={columns}
        data={dataSource}
        selectedRows={selectedRows}
        total={total}
        onPaginationChange={(page: any) => setPage(page)}
        onSearch={(text: string) => setSearch(text)}
        onSortChange={(sortData: any) => setSort(sortData)}
        loading={loading}
        onRowsPerPageChange={setPerPage}
        onRowSelected={({
          selectedRowKeys,
          allSelected,
          unselectedRowKeys,
          dispatcher,
        }: any) => {
          if (canSkipOnRowSelected(dispatcher)) return

          setSelectedRows(allSelected ? unselectedRowKeys : selectedRowKeys)
          setAllSelected(allSelected)
        }}
        locale={translations}
        countIcon={faBooks}
        initialRowsPerPage={10}
        simpleSelectAll
        onFastActionSelect={(selected: any) => setFastFilters(selected)}
        responsive
      />
      <ConfirmationModal
        show={showDeleteConfirmationModal}
        title=""
        text={t('administration.modal.participantDeleteConfirmation', {
          name: selected?.user.fullName.trim(),
        })}
        acceptButtonText={t('components.modal.buttons.delete')}
        onAccept={() => selected && handlePreDelete(selected)}
        onClose={() => setShowDeleteConfirmationModal(false)}
      />
      <ConfirmationModal
        show={showDeleteCertificateConfirmationModal}
        title=""
        text={t(
          joinKeys(
            prefixTranslation,
            'participants.certificates.modal.deleteConfirmation'
          )
        )}
        acceptButtonText={t('components.modal.buttons.delete')}
        onAccept={() => selected && handlePreDeleteCertificate(selected)}
        onClose={() => setShowDeleteCertificateConfirmationModal(false)}
      />
    </div>
  )
}

export default Table
